import { useQueryClient } from '@tanstack/react-query';
import { merge } from 'lodash';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { type Clip, ClipStatus } from '@air/api/types';

import { useEventSubscription } from '~/hooks/useEventSubscription';
import { updateCentralizedClipAction } from '~/store/centralizedClip/actions';
import { getClipKey } from '~/swr-hooks/clips/useClip';
import { getPrivateClipMetadata } from '~/swr-hooks/clips/usePrivateClipMetadata';
import { type EventSubscriptionArgs } from '~/types/sockets';
import { useUpdateAssetInAllViews } from '~/utils/mutateUtils/GalleryAssets';
import { getBoardIdFromPath, getClipIdFromPath } from '~/utils/PathUtils';

export type UseHandleClipUpdatedProps = {
  workspaceId: string | undefined;
} & ({ clipIds: string[] } | { clipId: string | undefined });

export const useHandleClipUpdated = (props: UseHandleClipUpdatedProps) => {
  const { workspaceId } = props;
  const dispatch = useDispatch();
  const { updateAssetInAllViews } = useUpdateAssetInAllViews();
  const queryClient = useQueryClient();

  const event = useMemo<EventSubscriptionArgs<'System.Clip.updated'> | undefined>(() => {
    if ('clipIds' in props) {
      return props.clipIds.length > 0 ? { eventType: 'System.Clip.updated' } : undefined;
    }

    return props.clipId
      ? {
          eventType: 'System.Clip.updated',
          pattern: {
            clipId: props.clipId,
          },
        }
      : undefined;
  }, [props]);

  useEventSubscription({
    currentWorkspaceId: workspaceId,
    event,
    options: {
      onUpdate: ({ data: { clipId, updates: clip } }) => {
        if ('clipIds' in props && !props.clipIds.includes(clipId)) {
          return;
        }

        const boardId = getBoardIdFromPath(window.location.pathname);

        /**
         * When an clip has been updated (in this workspace),
         * update it all views (gallery, table, kanban)
         */
        updateAssetInAllViews({
          asset: { id: clipId },
          parentBoardId: boardId,
          updateFn: (existingAsset: Clip) => merge(existingAsset, clip),
        });

        const currentClipId = getClipIdFromPath(window.location.pathname);

        if (clip.status === ClipStatus.transcoded) {
          queryClient.invalidateQueries({ queryKey: getPrivateClipMetadata(clipId) });
          if (workspaceId) {
            queryClient.invalidateQueries({ queryKey: getClipKey({ clipId, workspaceId }) });
          }
        }

        /**
         * If it's the current clip the user is looking at,
         * updated centralized clip too.
         * The reason we check clipId instead of clip.id
         * is because sometimes clip doesn't have id
         */
        if (currentClipId === clipId) {
          dispatch(
            updateCentralizedClipAction({
              clip: {
                id: clipId,
                ...clip,
              },
            }),
          );
        }
      },
    },
  });
};
